// @ai-generated 2026-08-13T07:38:21+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import {
    type ApiContainerAction,
    EnumContainerActionStatus,
    type EnumContainerOrderAction,
    isBooked,
    isOpenForBooking,
} from '@Model';
import { isLinked } from '@synqo/frontend-component-library';

/**
 * The planned rows of one container type that share what is done with it and what it holds.
 *
 * The group exists only on screen: the database keeps one row per container, and the count is derived
 * from them rather than stored.
 */
export interface PlannedContainerGroup {
    /** What makes the group, and what tells two groups of one type apart on screen. */
    key: string;
    /** The container type's id, which is what the planning and marking routes take. */
    id: string;
    label: string;
    action: EnumContainerOrderAction;
    actions: ApiContainerAction[];
    /** Number of rows, which is the count shown and edited on the group. */
    quantity: number;
    /** Rows a reduction may remove, in the order it takes them; a booked or confirmed row is never among them. */
    removableActions: ApiContainerAction[];
    /** Rows that can still be sent into a booking. */
    openActions: ApiContainerAction[];
}

/** Whether the row still waits for its container type, which leaves it out of every group. */
// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function hasNoContainerType({ container_type }: ApiContainerAction): boolean {
    return !isLinked(container_type);
}

/**
 * Groups the planned rows by container type, action and material, oldest row first within a group. A row
 * without a container type belongs to no group.
 *
 * Sorting is stable, so rows planned in the same call — they share a creation instant — keep the order
 * the server listed them in. The creation date is an ISO instant in UTC and compares as text.
 */
export function toContainerActionGroups(actions: readonly ApiContainerAction[]): PlannedContainerGroup[] {
    const byPlannedAt = actions
        .filter((action) => !hasNoContainerType(action))
        .sort((one, other) => one.created_at.localeCompare(other.created_at));

    const rowsByGroup = new Map<string, ApiContainerAction[]>();

    byPlannedAt.forEach((action) => {
        const key = toGroupKey(action);
        const rows = rowsByGroup.get(key);

        if (rows === undefined) {
            rowsByGroup.set(key, [action]);
            return;
        }

        rows.push(action);
    });

    return [...rowsByGroup.entries()].map(([key, rows]) => toGroup(key, rows));
}

/** The booked container actions of the group that are gone since the edit started. */
// @ai-generated 2026-10-27T11:24:03+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function removedBookedActions(
    group: PlannedContainerGroup,
    atEditStart: readonly ApiContainerAction[],
    current: readonly ApiContainerAction[],
): ApiContainerAction[] {
    const kept = new Set(current.map((action) => action.id));

    return atEditStart.filter(
        (action) =>
            isBooked(action.booking_status) &&
            !kept.has(action.id) &&
            !action.exchange_id &&
            toGroupKey(action) === group.key,
    );
}

/**
 * What makes a group: the container type together with what is done to it and what it holds. A type
 * that is delivered and collected on one order is two lines, and so is one that holds two materials —
 * one line over both would name one action or material for rows that carry the other, and count them
 * as if they were the same thing. Rows without a material are a group of their own.
 */
function toGroupKey({ action, container_type, content }: ApiContainerAction): string {
    return `${action}:${container_type.foreign_key ?? ''}:${content.foreign_key ?? ''}`;
}

function toGroup(key: string, rows: ApiContainerAction[]): PlannedContainerGroup {
    const first = rows[0];

    return {
        key,
        id: first.container_type.foreign_key ?? '',
        label: first.container_type.display_string,
        action: first.action,
        actions: rows,
        quantity: rows.length,
        removableActions: toRemovableActions(rows),
        openActions: rows.filter((action) => isOpenForBooking(action.booking_status)),
    };
}

/**
 * The container actions that go when the user lowers the count, in this order: those without a container,
 * the other open ones, booked ones. Within each group the newest goes first.
 *
 * Confirmed container actions stay, because the server refuses to delete them. A booked one the server
 * takes back.
 */
// @ai-generated 2026-08-20T16:04:00+02:00 by kai.cissarek@synqony.com using Claude Opus 5
function toRemovableActions(rows: readonly ApiContainerAction[]): ApiContainerAction[] {
    const newestFirst = [...rows]
        .reverse()
        .filter((action) => action.booking_status !== EnumContainerActionStatus.ToBeBooked);

    const rank = (action: ApiContainerAction) => {
        if (isBooked(action.booking_status)) {
            return 2;
        }

        return action.container.foreign_key ? 1 : 0;
    };

    // Sorting is stable, so the newest-first order survives inside each rank.
    return newestFirst.sort((one, other) => rank(one) - rank(other));
}
